import React, { useEffect, useRef, useState } from 'react';
import './SignupLoginModal.scss';
import { getCookie } from '../utils/utils';
import LoginOrSignup from './LoginOrSignup';
import Login from './Login';
import Signup from './Signup';
import EmailVerification from './EmailVerification';
import AuthHeader from './AuthHeader';
import { useTranslation } from 'react-i18next';
import ForgotPassword from './ForgotPassword';
import PriceEstimations from '../../pages/PriceEstimations';
import { clearPendingDraftChat } from '../utils/pendingDraftChat';
import { sessionStore } from '../utils/safeStorage';
import { setReportContext, trackStep } from '../utils/errorReporting';

type Props = {
  toggleSignupLoginModalVisible: () => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  isLoggedIn: boolean;
  onLoginSuccess: (expiresIn: number, marketingConsentShown: boolean) => void;
  isLandingPage?: boolean;
  setIsAdmin: (isAdmin: boolean) => void;
  setResponseEmail: (email: string) => void;
  initialEmail?: string;
  claimToken?: string;
  onLogoutStart?: () => void;
  onLogoutSuccess?: () => void;
};

const SignupLoginModal = (props: Props) => {
  const {
    toggleSignupLoginModalVisible,
    setIsLoggedIn,
    isLoggedIn,
    onLoginSuccess,
    isLandingPage,
    setIsAdmin,
    setResponseEmail,
    initialEmail,
    claimToken,
    onLogoutStart,
    onLogoutSuccess,
  } = props;
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState(initialEmail || '');
  const [showLoginView, setShowLoginView] = useState(false);
  const [showSignupView, setShowSignupView] = useState(false);
  const [showForgotPasswordView, setShowForgotPasswordView] = useState(false);
  const [verificationTitle, setVerificationTitle] = useState('');
  const [
    showEmailVerificationCodeInputView,
    setShowEmailVerificationCodeInputView,
  ] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [loginError, setLoginError] = useState(''); // Add this line
  const [authError, setAuthError] = useState(''); // Add this line for OAuth errors
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [verificationError, setVerificationError] = useState(''); // Add this line
  const authCompletedRef = useRef(false);
  // True while a login or confirm-email request is out: its response may still sign the user in.
  const authRequestInFlightRef = useRef(false);
  // The code step was reached from a login of an unconfirmed account, whose old code is usually gone.
  const [resendCodeOnOpen, setResendCodeOnOpen] = useState(false);

  // Surface a failed redirect-flow Google login: the backend lands the user on /?login_error=<reason> (iOS, where the popup tab navigates back here instead of closing).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loginErr = params.get('login_error');
    if (!loginErr) return;
    setAuthError(
      loginErr === 'exists_with_another_authentication_method'
        ? t('signupLoginModal.errors.existsWithAnotherAuthenticationMethod')
        : t('signupLoginModal.errors.genericOAuth'),
    );
    params.delete('login_error');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoginSuccess = (expiresIn: number, marketingConsentShown: boolean) => {
    authCompletedRef.current = true;
    onLoginSuccess(expiresIn, marketingConsentShown);
  };

  /**
   * Close the auth modal. Dismissed before logging in, it also abandons the
   * Send gate: the draft chat parked at it (JAA-325) is dropped at once, so an
   * abandoned message never reopens on a shared device nor is sent by a login
   * that still lands (JAA-447; the chat then shows its "Press Send" hint), and
   * the deferred marketing consent goes too, so a later same-session header
   * signup still gets the prompt. The consent deferral stays while a login
   * request is out: it can still sign the user in, and the consent modal must
   * not open over the chat. The chat on screen keeps its text; Send parks it
   * again.
   */
  const handleClose = () => {
    if (!authCompletedRef.current && !isLoggedIn) {
      clearPendingDraftChat();
      if (!authRequestInFlightRef.current) {
        try {
          sessionStore.removeItem('marketing_consent_deferred');
        } catch {
          // Storage blocked: nothing was deferred in this tab.
        }
      }
    }

    toggleSignupLoginModalVisible();
  };

  // The Escape listener binds once and reads this render's handleClose through the ref (CouldBuyFlow closeFlowRef).
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  /**
   * Escape closes the auth modal exactly like its X, through handleClose, so a
   * dismissed Send gate still drops the parked draft and the consent deferral
   * (the deferral stays while a login request is out).
   * Window capture runs before every document listener and stopPropagation ends
   * the event there, so the Escape handlers of flows underneath (CouldBuyFlow,
   * AddMyPlaceFlow, EditListingModal, SearchInput) never see it. Bound for the
   * modal's lifetime: it mounts only while open. No step inside uses Escape.
   */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      handleCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  /**
   * An account that signed up but never entered its code (the verify screen was
   * lost) logs in with the right password and gets email_not_confirmed. Send it
   * to the code step for the same email instead of dead-ending at "Check
   * password", with a fresh code on its way (the old one has usually expired);
   * confirm-email needs only email and code.
   */
  const handleEmailNotConfirmed = () => {
    setLoginError('');
    setAuthError('');
    setVerificationCode('');
    setVerificationError('');
    setResendCodeOnOpen(true);
    setShowLoginView(false);
    setShowEmailVerificationCodeInputView(true);
  };

  const handleForgotPasswordClick = () => {
    setShowLoginView(false);
    setShowForgotPasswordView(true);
    setLoginError('');
    setAuthError('');
  };

  const handleBack = () => {
    if (showForgotPasswordView) {
      setShowForgotPasswordView(false);
      setShowLoginView(true);
      setLoginError('');
      setAuthError('');
    } else if (showLoginView) {
      setShowLoginView(false);
      setLoginError('');
      setAuthError('');
    } else if (showSignupView) {
      setShowSignupView(false);
      setLoginError('');
      setAuthError('');
    } else if (showEmailVerificationCodeInputView) {
      setShowEmailVerificationCodeInputView(false);
      setResendCodeOnOpen(false);
    } else {
      toggleSignupLoginModalVisible();
    }
  };

  const showBackButton =
    showLoginView ||
    showSignupView ||
    showForgotPasswordView ||
    showEmailVerificationCodeInputView;

  /**
   * Name of the sign-in view on screen, used to tag error reports and to record
   * which steps visitors reach (JAA-388).
   */
  const authStepName = () => {
    if (showForgotPasswordView) return 'forgot-password';
    if (showLoginView) return 'login';
    if (showSignupView) return 'signup';
    if (showEmailVerificationCodeInputView) return 'verification-code';
    return 'email';
  };
  const authStep = authStepName();

  useEffect(() => {
    setReportContext({ authStep });
    trackStep('auth-view', { view: authStep });
    return () => setReportContext({ authStep: null });
  }, [authStep]);

  /**
   * Title for the header row. Each screen's heading moves up beside the close so
   * the two read as one bar instead of stacking. The verification screen swaps
   * its wording on the SMS fallback, which only that component knows, so it
   * reports its title up rather than having it guessed here.
   */
  const headerTitle = () => {
    if (showForgotPasswordView) {
      return t('signupLoginModal.forgotPassword.title');
    }
    if (showLoginView) {
      return t('signupLoginModal.login');
    }
    if (showSignupView) {
      return t('signupLoginModal.signup.title');
    }
    if (showEmailVerificationCodeInputView) {
      return verificationTitle;
    }
    if (isLoggedIn) {
      return '';
    }
    return t('signupLoginModal.loginOrSignup');
  };

  /**
   * Modifier classes for the modal card. Emits exactly the set the previous
   * inline ternary chain produced: the per-view size modifiers, plus the
   * info-box flag which is the complement of the first-screen state.
   */
  const modalClassName = () => {
    const classNames = ['signup-login-modal'];
    if (showSignupView) {
      classNames.push('signup-login-modal--signup');
    }
    if (showEmailVerificationCodeInputView) {
      classNames.push('signup-login-modal--email-verification');
    }
    if (isLoggedIn) {
      classNames.push('signup-login-modal--logout-view');
    }
    if (showBackButton) {
      classNames.push('signup-login-modal--has-back');
    }
    const isFirstScreen = !showBackButton && !isLoggedIn;
    classNames.push(
      isFirstScreen
        ? 'signup-login-modal--login'
        : 'signup-login-modal--no-info-box-view',
    );
    return classNames.join(' ');
  };

  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return t('signupLoginModal.passwordRequirements.length');
    }
    if (!/[A-Z]/.test(password)) {
      return t('signupLoginModal.passwordRequirements.uppercase');
    }
    if (!/[a-z]/.test(password)) {
      return t('signupLoginModal.passwordRequirements.lowercase');
    }
    if (!/[0-9]/.test(password)) {
      return t('signupLoginModal.passwordRequirements.number');
    }
    return '';
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Virheellinen sähköpostiosoite.';
  };

  /**
   * Submits the emailed (or texted) code. too_many_attempts means the code is
   * burned and verification_code_expired that it timed out: either way only a
   * resend can continue, which the expired message tells the user. A confirm
   * that signs the user in also hands the address up through setResponseEmail,
   * like every other login path: /confirm-email answers without one, and the
   * shell would otherwise show no email (Profile) until a reload (JAA-449).
   */
  const handleSubmitEmailVerificationCode = async () => {
    setIsVerifyingEmail(true);
    authRequestInFlightRef.current = true;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/confirm-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({
            email: email.toLowerCase(),
            verification_code: verificationCode,
            language: i18n.language
          }),
          credentials: 'include',
        },
      );

      if (response.ok) {
        const data = await response.json();
        // email_already_confirmed (confirmed elsewhere meanwhile) sets no session: log in with the password instead.
        if (!data.expires_in) {
          setShowEmailVerificationCodeInputView(false);
          setResendCodeOnOpen(false);
          setShowLoginView(true);
          return;
        }
        setResponseEmail(email.toLowerCase());
        setIsLoggedIn(true);
        handleLoginSuccess(data.expires_in, data.marketing_consent_shown ?? true);
        toggleSignupLoginModalVisible();
      } else {
        const errorData = await response.json();
        if (errorData.error === 'invalid_code') {
          setVerificationError('Invalid verification code. Please try again.'); // Set error message
        } else if (errorData.error === 'too_many_attempts') {
          setVerificationError(
            t('signupLoginModal.emailVerification.tooManyAttempts'),
          );
        } else if (errorData.error === 'rate_limit_exceeded') {
          setVerificationError(
            t('signupLoginModal.emailVerification.rateLimitError'),
          );
        } else if (errorData.error === 'verification_code_expired') {
          setVerificationError(t('profile.codeExpired'));
        } else {
          console.error('Email confirmation code failed');
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsVerifyingEmail(false);
      authRequestInFlightRef.current = false;
    }
  };

  return (
    <div className={`signup-login-modal-overlay ${isLandingPage ? 'signup-login-modal-overlay--landing-page' : ''}`}>
      <div className={modalClassName()} onClick={(e) => e.stopPropagation()}>
        <div className="signup-login-modal__header">
          <AuthHeader
            title={headerTitle()}
            onBack={showBackButton ? handleBack : undefined}
            onClose={handleClose}
          />
        </div>
        <div className="signup-login-modal__body">
          {!showLoginView &&
            !showSignupView &&
            !showForgotPasswordView &&
            !showEmailVerificationCodeInputView &&
            !isLoggedIn && (
              <LoginOrSignup
                email={email}
                toggleSignupLoginModalVisible={handleClose}
                setShowLoginView={setShowLoginView}
                setShowSignupView={setShowSignupView}
                validateEmail={validateEmail}
                onLoginSuccess={handleLoginSuccess}
                setIsLoggedIn={setIsLoggedIn}
                setEmail={setEmail}
                setResponseEmail={setResponseEmail}
                authError={authError}
                setAuthError={setAuthError}
              />
            )}
          {/*         {isLoggedIn && (
            <>
              <PriceEstimations />
              <Logout
                toggleSignupLoginModalVisible={toggleSignupLoginModalVisible}
                setIsLoggedIn={setIsLoggedIn}
                onLogoutStart={onLogoutStart}
                onLogoutSuccess={onLogoutSuccess}
              />
            </>
          )} */}
          {showLoginView && (
            <Login
              email={email}
              loginError={loginError || authError}
              toggleSignupLoginModalVisible={handleClose}
              onLoginSuccess={handleLoginSuccess}
              setLoginError={setLoginError}
              validatePassword={validatePassword}
              onForgotPasswordClick={handleForgotPasswordClick}
              onEmailNotConfirmed={handleEmailNotConfirmed}
              onRequestInFlightChange={(inFlight) => {
                authRequestInFlightRef.current = inFlight;
              }}
              setIsAdmin={setIsAdmin}
              setResponseEmail={setResponseEmail}
            />
          )}
          {showSignupView && (
            <Signup
              email={email}
              setEmail={setEmail}
              setShowSignupView={setShowSignupView}
              setShowLoginView={setShowLoginView}
              validatePassword={validatePassword}
              setShowEmailVerificationCodeInputView={
                setShowEmailVerificationCodeInputView
              }
              setLoginError={setLoginError}
              setAuthError={setAuthError}
              validateEmail={validateEmail}
            />
          )}
          {showForgotPasswordView && (
            <ForgotPassword
              email={email}
              setEmail={setEmail}
              validateEmail={validateEmail}
              setShowForgotPasswordView={setShowForgotPasswordView}
              setShowLoginView={setShowLoginView}
            />
          )}
          {showEmailVerificationCodeInputView && (
            <EmailVerification
              email={email}
              verificationCode={verificationCode}
              setVerificationCode={setVerificationCode}
              onSubmitCode={handleSubmitEmailVerificationCode}
              isVerifying={isVerifyingEmail}
              verificationError={verificationError}
              onTitleChange={setVerificationTitle}
              resendOnOpen={resendCodeOnOpen}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupLoginModal;
